/**
 * Image utility functions for handling image URLs and processing
 */

/**
 * Get full image URL from relative path using environment variables
 * @param {string} imagePath - The relative image path (e.g., '/uploads/profiles/image.jpg')
 * @returns {string|null} Full URL or null if no path provided
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Otherwise, prepend the server base URL from environment
  const baseUrl = import.meta.env.VITE_SERVER_BASE_URL || 'http://localhost:4000';
  return `${baseUrl}${imagePath}`;
};

/**
 * Get profile image URL with fallback
 * @param {string} imagePath - The profile image path
 * @param {string} fallbackUrl - Optional fallback URL
 * @returns {string|null} Full URL or null
 */
export const getProfileImageUrl = (imagePath, fallbackUrl = null) => {
  const url = getImageUrl(imagePath);
  return url || fallbackUrl;
};

/**
 * Validate if an image URL is accessible
 * @param {string} url - Image URL to check
 * @returns {Promise<boolean>} True if image loads successfully
 */
export const validateImageUrl = async (url) => {
  if (!url) return false;
  
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Get default avatar URL based on user initials
 * @param {string} firstName - User's first name
 * @param {string} lastName - User's last name
 * @param {number} size - Avatar size in pixels
 * @returns {string} Avatar URL
 */
export const getDefaultAvatarUrl = (firstName, lastName, size = 128) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || 'U';
  return `https://ui-avatars.com/api/?name=${initials}&size=${size}&background=6366f1&color=ffffff`;
};
